/**
 * ConsentService — gerencia consentimento LGPD para tracking analytics.
 *
 * Persistência: localStorage chave `xegueiConsent` = "accepted" | "denied".
 * Subscribers podem reagir a mudanças (útil para Analytics inicializar/parar).
 *
 * Arquitetura preparada para evoluir para consent granular (múltiplas
 * categorias) sem refactor — basta estender o estado interno.
 */

export type ConsentStatus = 'accepted' | 'denied' | 'unknown';

const STORAGE_KEY = 'xegueiConsent';

type Listener = (status: ConsentStatus) => void;
const listeners = new Set<Listener>();

function readRaw(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeRaw(value: ConsentStatus): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // ignore
  }
}

function notify(status: ConsentStatus): void {
  listeners.forEach((l) => {
    try {
      l(status);
    } catch (err) {
      console.warn('[ConsentService] listener falhou', err);
    }
  });
}

export const ConsentService = {
  getStatus(): ConsentStatus {
    const v = readRaw();
    if (v === 'accepted' || v === 'denied') return v;
    return 'unknown';
  },

  hasConsent(): boolean {
    return ConsentService.getStatus() === 'accepted';
  },

  isPending(): boolean {
    return ConsentService.getStatus() === 'unknown';
  },

  accept(): void {
    writeRaw('accepted');
    notify('accepted');
  },

  reject(): void {
    writeRaw('denied');
    notify('denied');
  },

  /** Reset (útil para testes / opção "rever consentimento"). */
  reset(): void {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    notify('unknown');
  },

  /** Inscreve listener. Retorna função de unsubscribe. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export default ConsentService;
